import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Reusable date-range picker with quick presets + a custom range. Generic — use
// in any report or filter. `value` = { preset, start, end } (start/end = YYYY-MM-DD);
// `onChange(next)` gets the resolved range. presetRange(preset) is exported so
// callers can seed initial state.
const PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "this_month", label: "Current month" },
  { id: "last_month", label: "Last month" },
  { id: "this_quarter", label: "Current quarter" },
  { id: "last_quarter", label: "Last quarter" },
  { id: "custom", label: "Custom range" },
];

const fmt = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const presetRange = (preset) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case "today":
      return { start: fmt(now), end: fmt(now) };
    case "yesterday": {
      const d = new Date(y, m, now.getDate() - 1);
      return { start: fmt(d), end: fmt(d) };
    }
    case "this_month":
      return { start: fmt(new Date(y, m, 1)), end: fmt(now) };
    case "last_month":
      return { start: fmt(new Date(y, m - 1, 1)), end: fmt(new Date(y, m, 0)) };
    case "this_quarter": {
      const q = Math.floor(m / 3);
      return { start: fmt(new Date(y, q * 3, 1)), end: fmt(now) };
    }
    case "last_quarter": {
      const q = Math.floor(m / 3);
      return { start: fmt(new Date(y, q * 3 - 3, 1)), end: fmt(new Date(y, q * 3, 0)) };
    }
    default:
      return { start: "", end: "" };
  }
};

export const defaultRange = (preset = "this_month") => ({ preset, ...presetRange(preset) });

const DateRangePicker = ({ value, onChange }) => {
  const preset = value?.preset || "this_month";
  const setPreset = (p) => {
    if (p === "custom") onChange({ preset: "custom", start: value?.start || "", end: value?.end || "" });
    else onChange({ preset: p, ...presetRange(p) });
  };
  return (
    <div className="space-y-2">
      <Select value={preset} onValueChange={setPreset}>
        <SelectTrigger className="h-9" data-testid="date-range-preset"><SelectValue /></SelectTrigger>
        <SelectContent>
          {PRESETS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {preset === "custom" && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">From</Label>
            <Input type="date" value={value?.start || ""}
              onChange={(e) => onChange({ ...value, preset: "custom", start: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">To</Label>
            <Input type="date" value={value?.end || ""}
              onChange={(e) => onChange({ ...value, preset: "custom", end: e.target.value })} />
          </div>
        </div>
      )}
      {preset !== "custom" && value?.start && (
        <p className="text-xs text-slate-400">{value.start} → {value.end}</p>
      )}
    </div>
  );
};

export default DateRangePicker;
